import React, { useEffect, useState } from 'react';
import {
  requiredField,
  spedNoteSuccess,
  disablePastDate,
  PaperPropsWithoutScrollbar,
} from 'constants';
import {
  onSave,
  currentDate,
  emptyFunction,
  getOptionLabel,
  getCurrentTime,
  fieldValidation,
  useNotification,
  getDialogHeading,
  formatTimeZoneDate,
  dropDownValidation,
  renderSaveCancelButtons,
} from 'utils/commonUtils';
import * as yup from 'yup';
import {
  putSpedNote,
  postSpedNote,
  postSpedNoteBulk,
  postSignOffSpedNote,
} from '../../services/spedNotesApi';
import MDBox from 'components/MDBox';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import { useForm } from 'react-hook-form';
import Typography from '@mui/material/Typography';

import {
  setIsNewSpedNote,
  setIsBulkSpedNote,
  setSingleSpedNote,
  setSelectedStudents,
} from '../../store/spedNotesSlice/spedNoteSlice';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import { GridForm, AccordianContainer } from 'components/Ui/styled';
import { FormInputText } from 'components/Common/Forms/FormTextField';
import { SpedNotesStudentsAccordionSummary } from './spedNotesStyled';
import { FormInputDate } from 'components/Common/Forms/FormInputDate';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import { getSearchedStudents } from 'layouts/students/service/studentsApi';
import { formatServiceDateTime, SignaturePair } from '../spedNotesCommonUtils';
import { FormInputAutoCompleteSearch } from 'components/Common/Forms/FormAutoCompleteSearch';
import { usePermissionCustomHook } from 'layouts/users/userPermissions/utils/permissionsUtils';

const SpedNoteDialog = (props) => {
  const {
    open,
    setOpen,
    spedNoteForm,
    filterOptions,
    setSelectedIds,
    selectedStudent,
    fetchSpedNotes = emptyFunction,
    setSpedNoteForm = emptyFunction,
  } = props;

  const {
    notes,
    location,
    archived,
    createdAt,
    studentId,
    updatedAt,
    esignature,
    resourceId,
    serviceDate,
    serviceType,
    sessionStatus,
    serviceProvider,
    noteSignatureAgree,
    genEdEsignature,
    genEdSignatureAgree,
    seifEsignature,
    seifSignatureAgree,
    serviceDurationMinutes,
  } = spedNoteForm || {};

  const {
    singleSpedNote: isSingleSpedNote,
    selectedStudents: selectedBulkStudents = [],
    isNewSpedNote,
    isBulkSpedNote,
  } = useSelector((state) => state?.spedNotes || {});

  const schema = React.useMemo(() => {
    const createSignatureSchema = () =>
      yup
        .string()
        .required(requiredField)
        .matches(/^[a-zA-Z\s]+$/, 'this signature must contain only letters');
    const createAgreementSchema = () =>
      yup.boolean().oneOf([true], '*You must agree before submitting');

    return yup.object().shape({
      notes: fieldValidation(4000),
      location: dropDownValidation,
      serviceType: dropDownValidation,
      sessionStatus: dropDownValidation,
      serviceProvider: dropDownValidation,
      serviceTime: yup.string().required(requiredField).nullable(),
      serviceDate: yup
        .date()
        .nullable()
        .when([], {
          is: () => isNewSpedNote === true,
          then: (schema) =>
            schema
              .required(requiredField)
              .typeError(requiredField)
              .min(new Date(new Date().setHours(0, 0, 0, 0)), disablePastDate),
          otherwise: (schema) => schema.nullable(),
        }),
      serviceDurationMinutes: yup
        .string()
        .required(requiredField)
        .nullable()
        .test(
          'non-negative-number',
          'Duration cannot be negative',
          (value) => Number(value) >= 0,
        ),
      esignature: createSignatureSchema(),
      noteSignatureAgree: createAgreementSchema(),
      // genEdEsignature: createSignatureSchema(),
      // genEdSignatureAgree: createAgreementSchema(),
      // seifEsignature: createSignatureSchema(),
      // seifSignatureAgree: createAgreementSchema(),
      seifEsignature: yup.string().when([], {
        is: () => isNewSpedNote === false,
        then: createSignatureSchema(),
        otherwise: () => yup.string().notRequired(),
      }),

      seifSignatureAgree: yup.boolean().when([], {
        is: () => isNewSpedNote === false,
        then: createAgreementSchema(),
      }),
      studentId:
        isBulkSpedNote || selectedStudent
          ? yup.string().notRequired()
          : yup.string().required(requiredField),
    });
  }, [isBulkSpedNote, selectedStudent, isNewSpedNote]);

  const dispatch = useDispatch();
  const { NotificationPopup, setOpenNotification, setNotificationMessage } =
    useNotification();
  const canSignOffSpedNotes = usePermissionCustomHook('CAN_SIGNOFF_SPED_NOTES');

  const {
    locations = [],
    providers = [],
    serviceTypes = [],
    sessionStatuses = [],
  } = filterOptions || {};

  const [student, setStudent] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [studentOptions, setStudentOptions] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [isStudentSearchLoading, setIsStudentSearchLoading] = useState(false);

  useEffect(() => {
    if (studentSearchQuery?.length >= 1) fetchSearchedStudents();
  }, [studentSearchQuery]);

  const fetchSearchedStudents = async () => {
    try {
      setIsStudentSearchLoading(true);
      const searchedStudents = await getSearchedStudents(
        studentSearchQuery,
        true,
        false,
        20,
      );
      setStudentOptions(searchedStudents?.items || []);
      setIsStudentSearchLoading(false);
    } catch (error) {
      setIsStudentSearchLoading(false);
    }
  };

  const extractTimeFromDateTime = (dateTime) => {
    if (!dateTime) return '';
    const d = new Date(dateTime);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const defaultValue = {
    notes: notes || '',
    location: location || '',
    esignature: esignature || '',
    serviceDate: serviceDate ? new Date(serviceDate) : null,
    serviceTime: serviceDate
      ? extractTimeFromDateTime(serviceDate)
      : getCurrentTime(),
    serviceType: serviceType || '',
    sessionStatus: sessionStatus || '',
    serviceProvider: serviceProvider || '',
    studentId: spedNoteForm?.studentId || selectedStudent?.studentId || '',
    noteSignatureAgree: noteSignatureAgree || false,
    serviceDurationMinutes: serviceDurationMinutes ?? 30,
    seifEsignature: seifEsignature || '',
    seifSignatureAgree: seifSignatureAgree || false,
    genEdEsignature: genEdEsignature || '',
    genEdSignatureAgree: genEdSignatureAgree || false,
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
    resolver: yupResolver(schema),
  });

  const { reset, handleSubmit, control, formState, setValue } = useFunction;
  const { errors } = formState;

  useEffect(() => {
    if (open) {
      reset(defaultValue);
    }
  }, [open]);

  const handleClose = () => {
    setOpen(false);

    // Reset all form values
    reset(defaultValue);

    // Clear all internal states
    setStudent(null);
    setSpedNoteForm({});
    setStudentOptions([]);
    setStudentSearchQuery('');
    setIsStudentSearchLoading(false);
    dispatch(setIsNewSpedNote(true));
    dispatch(setSingleSpedNote(false));
    dispatch(setIsBulkSpedNote(false));
  };

  const mapOptions = (arr) =>
    arr?.map((item) => ({ id: item, label: item, value: item }));

  const locationsOptions = mapOptions(locations);
  const providersOptions = mapOptions(providers);
  const serviceTypesOptions = mapOptions(serviceTypes);
  const sessionStatusesOptions = mapOptions(sessionStatuses);

  const onSubmit = (data) => {
    const {
      studentId,
      studentIds,
      serviceDate,
      serviceTime,
      multipleStudents,
      ...restData
    } = data;

    const formatAsLocalDate = (date) => {
      if (!date) return null;
      const d = new Date(date);
      d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
      return d.toISOString().split('T')[0];
    };

    const payload = {
      ...restData,
      // serviceDate: formatAsLocalDate(serviceDate),
      serviceDate: formatServiceDateTime(serviceDate, serviceTime),
      ...(isBulkSpedNote
        ? { studentIds: selectedBulkStudents }
        : (student?.studentId || selectedStudent?.studentId) && {
            studentId: student?.studentId || selectedStudent?.studentId,
          }),
    };

    onSave(
      spedNoteSuccess,
      isNewSpedNote,
      isSingleSpedNote ? postSpedNote : postSpedNoteBulk,
      postSignOffSpedNote,
      payload,
      resourceId,
      fetchSpedNotes,
      handleClose,
      setOpenNotification,
      setNotificationMessage,
      dispatch,
      setLoadingButton,
    );
    dispatch(setSelectedStudents([]));
    if (setSelectedIds) {
      setSelectedIds([]);
    }
  };

  const handleStudentSelect = (val) => {
    const studentId = val?.studentId || '';
    setStudent(val);
    setValue('studentId', studentId, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleAccordionChange = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      <Dialog open={open} PaperProps={PaperPropsWithoutScrollbar}>
        {getDialogHeading('SPED Note', handleClose)}

        {isBulkSpedNote && (
          <>
            <GridForm container spacing={2}>
              <Grid item xs={12} md={12}>
                <AccordianContainer
                  expanded={isExpanded}
                  defaultExpanded={false}
                  onChange={handleAccordionChange}
                >
                  <SpedNotesStudentsAccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                  >
                    <Typography variant="d7">Selected Students</Typography>
                  </SpedNotesStudentsAccordionSummary>

                  <AccordionDetails>
                    <Typography variant="d7">
                      {selectedBulkStudents.join(', ')}
                    </Typography>
                  </AccordionDetails>
                </AccordianContainer>
              </Grid>
            </GridForm>
            <MDBox mb={1}></MDBox>
          </>
        )}

        <GridForm container spacing={2}>
          {(!isNewSpedNote || selectedStudent?.studentId) && (
            <Grid item xs={12} sm={6}>
              <FormInputText
                type="text"
                readOnly={true}
                name="studentId"
                label="Student ID"
                control={control}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <FormInputDate
              control={control}
              name="serviceDate"
              value={serviceDate}
              label="Service Date"
              readOnly={!isNewSpedNote}
              errors={!!errors?.serviceDate}
              helperText={errors?.serviceDate?.message}
              minDate={isNewSpedNote ? currentDate : null}
              // errors={isNewSpedNote ? !!errors?.serviceDate : false}
              // helperText={isNewSpedNote ? errors?.serviceDate?.message : ''}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormInputText
              type="time"
              shrink={true}
              control={control}
              name="serviceTime"
              label="Service Time"
              readOnly={!isNewSpedNote}
              errors={!!errors.serviceTime}
              helperText={errors?.serviceTime?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInputText
              type="number"
              control={control}
              readOnly={!isNewSpedNote}
              name="serviceDurationMinutes"
              label="Service Duration (minutes)"
              errors={!!errors?.serviceDurationMinutes}
              helperText={errors?.serviceDurationMinutes?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInputDropdown
              control={control}
              name="location"
              label="Location"
              readOnly={!isNewSpedNote}
              options={locationsOptions}
              errors={!!errors?.location}
              helperText={errors?.location?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInputDropdown
              control={control}
              name="serviceProvider"
              label="Service Provider"
              readOnly={!isNewSpedNote}
              options={providersOptions}
              errors={!!errors?.serviceProvider}
              helperText={errors?.serviceProvider?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInputDropdown
              control={control}
              name="sessionStatus"
              label="Session Status"
              readOnly={!isNewSpedNote}
              options={sessionStatusesOptions}
              errors={!!errors?.sessionStatus}
              helperText={errors?.sessionStatus?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInputDropdown
              control={control}
              name="serviceType"
              label="Service Type"
              readOnly={!isNewSpedNote}
              options={serviceTypesOptions}
              errors={!!errors?.serviceType}
              helperText={errors?.serviceType?.message}
            />
          </Grid>

          {!isBulkSpedNote && isNewSpedNote && !selectedStudent?.studentId && (
            <Grid item xs={12} sm={6}>
              <FormInputAutoCompleteSearch
                value={student}
                label="Search student"
                options={studentOptions}
                error={!!errors?.studentId}
                placeholder="Search student"
                getOptionLabel={getOptionLabel}
                setSelectedInput={handleStudentSelect}
                helperText={errors?.studentId?.message}
                isSearchloading={isStudentSearchLoading}
                setInputSearchQuery={setStudentSearchQuery}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <FormInputText
              required
              rows={2}
              type="text"
              name="notes"
              label="Notes"
              multiline={true}
              control={control}
              errors={!!errors?.notes}
              readOnly={!isNewSpedNote}
              helperText={errors?.notes?.message}
            />
          </Grid>

          <SignaturePair
            errors={errors}
            control={control}
            inputName="esignature"
            readOnly={!isNewSpedNote}
            checkboxName="noteSignatureAgree"
            inputLabel="SPED Provider Signature"
            inputPlaceholder="Enter your full name"
          />

          <SignaturePair
            errors={errors}
            control={control}
            readOnly={!isNewSpedNote}
            inputName="genEdEsignature"
            checkboxName="genEdSignatureAgree"
            inputLabel="Gen Ed Teacher Signature (optional)"
            inputPlaceholder="Enter Gen Ed teacher's full name"
          />

          {!isNewSpedNote && canSignOffSpedNotes && (
            <SignaturePair
              errors={errors}
              control={control}
              readOnly={Boolean(seifSignatureAgree)}
              inputName="seifEsignature"
              checkboxName="seifSignatureAgree"
              inputPlaceholder="Enter SEIF Signature"
              inputLabel="SEIF Electronic Signature (optional)"
            />
          )}

          {(!canSignOffSpedNotes || seifSignatureAgree) && (
            <Grid item xs={12} sm={6}></Grid>
          )}
          {(isNewSpedNote || canSignOffSpedNotes) &&
            !seifSignatureAgree &&
            renderSaveCancelButtons(
              handleClose,
              handleSubmit(onSubmit),
              loadingButton,
            )}
        </GridForm>
      </Dialog>
      <NotificationPopup />
    </>
  );
};

export default SpedNoteDialog;
